import React, { useState, useCallback, useEffect } from 'react';
import { GoogleMap, useJsApiLoader, DirectionsRenderer, InfoWindow } from '@react-google-maps/api';
import AdvancedMarker from './AdvancedMarker';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  MyLocation as MyLocationIcon,
  LocationOn as PickupIcon,
  Room as DestinationIcon
} from '@mui/icons-material';
import useMapColorScheme from '../../hooks/useMapColorScheme';
import { routePoint, withoutPlaceId, directionsLocation, hasPlaceIds, placeIdsPlausibel } from '../../utils/routePlaceIds';

// Standardoptionen für die Google Maps-Karte
const mapContainerStyle = {
  width: '100%',
  height: '400px'
};

const defaultCenter = {
  lat: 50.1109, // Frankfurt am Main, Deutschland
  lng: 8.6821
};

const defaultOptions = {
  fullscreenControl: false,
  streetViewControl: true,
  mapTypeControl: true,
  zoomControl: true,
  minZoom: 3,
  maxZoom: 18,
  // Map ID für Advanced Markers (benötigt für AdvancedMarkerElement)
  mapId: '8bb9c199f544a76'
};

/**
 * Eine wiederverwendbare Google Maps-Komponente für die Darstellung von Fahrten
 * 
 * @param {Object} props - Die Komponenteneigenschaften
 * @param {Object} props.pickupLocation - Die Abholortkoordinaten { lat, lng }
 * @param {Object} props.destinationLocation - Die Zielortkoordinaten { lat, lng }
 * @param {string} props.pickupAddress - Die Abholadresse als Text
 * @param {string} props.destinationAddress - Die Zieladresse als Text
 * @param {Object} props.currentLocation - Die aktuelle Position des Fahrers { lat, lng }
 * @param {string} props.height - Die Höhe der Karte (Standard: 400px)
 * @param {boolean} props.showDirections - Ob Richtungen angezeigt werden sollen
 * @param {boolean} props.showInfoWindows - Ob Info-Fenster angezeigt werden sollen
 * @param {string} props.pickupPlaceId - Place-ID des Abholorts (optional, Route ab dem Zugangspunkt)
 * @param {string} props.destinationPlaceId - Place-ID des Ziels (optional)
 */
const MapView = ({
  pickupLocation,
  destinationLocation,
  pickupAddress,
  destinationAddress,
  currentLocation,
  height = 400,
  showDirections = true,
  showInfoWindows = true,
  pickupPlaceId = null,
  destinationPlaceId = null
}) => {
  const [map, setMap] = useState(null);
  const mapScheme = useMapColorScheme();
  const [directions, setDirections] = useState(null);
  const [directionsError, setDirectionsError] = useState(null);
  const [selectedMarker, setSelectedMarker] = useState(null);
  
  // Google Maps API laden - Verwendung der globalen Definition aus index.js
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    // Verwende die globale Definition, um Referenzgleichheit sicherzustellen
    libraries: window.GOOGLE_MAPS_LIBRARIES,
    version: 'weekly' // Sicherstellen, dass die neueste Version geladen wird
  });
  
  // Bestimme den Kartenmittelpunkt basierend auf den verfügbaren Orten
  const getMapCenter = useCallback(() => {
    if (currentLocation) return currentLocation;
    if (pickupLocation) return pickupLocation;
    if (destinationLocation) return destinationLocation;
    return defaultCenter;
  }, [currentLocation, pickupLocation, destinationLocation]);
  
  // Bestimme die Kartengrenzen, um alle Marker einzuschließen
  const fitBounds = useCallback((map) => {
    if (!map) return;
    
    const bounds = new window.google.maps.LatLngBounds();
    let hasBounds = false;
    
    if (pickupLocation && pickupLocation.lat && pickupLocation.lng) {
      bounds.extend(pickupLocation);
      hasBounds = true;
    }
    
    if (destinationLocation && destinationLocation.lat && destinationLocation.lng) {
      bounds.extend(destinationLocation);
      hasBounds = true;
    }
    
    if (currentLocation && currentLocation.lat && currentLocation.lng) {
      bounds.extend(currentLocation);
      hasBounds = true;
    }
    
    if (hasBounds) {
      map.fitBounds(bounds);
      
      // Zoom-Level anpassen, wenn nur ein Punkt vorhanden ist
      if ((pickupLocation && !destinationLocation && !currentLocation) ||
          (!pickupLocation && destinationLocation && !currentLocation) ||
          (!pickupLocation && !destinationLocation && currentLocation)) {
        map.setZoom(15);
      }
    }
  }, [pickupLocation, destinationLocation, currentLocation]);
  
  // Richtungen berechnen
  const calculateDirections = useCallback(() => {
    if (!isLoaded || !pickupLocation || !destinationLocation) return;
    
    const directionsService = new window.google.maps.DirectionsService();

    // Place-ID statt Koordinate (wie Fahrer-App und Festpreis, Phase 2e):
    // sonst fängt die Route oft in der Zufahrt und zeigt einen Wendeumweg.
    const points = [
      routePoint(pickupLocation, pickupPlaceId),
      routePoint(destinationLocation, destinationPlaceId),
    ];
    const request = (pts, onDone) => directionsService.route(
      {
        origin: directionsLocation(pts[0]),
        destination: directionsLocation(pts[1]),
        travelMode: window.google.maps.TravelMode.DRIVING
      },
      onDone
    );
    const show = (result, status) => {
      if (status === window.google.maps.DirectionsStatus.OK) {
        setDirections(result);
        setDirectionsError(null);
      } else {
        setDirectionsError(`Fehler bei der Routenberechnung: ${status}`);
      }
    };
    const toLatLng = (p) => (p ? { lat: p.lat(), lng: p.lng() } : null);

    request(points, (result, status) => {
      if (!hasPlaceIds(points)) { show(result, status); return; }
      const legs = result?.routes?.[0]?.legs || [];
      const ok = status === window.google.maps.DirectionsStatus.OK &&
        placeIdsPlausibel(points, legs.map((leg) => ({
          start: toLatLng(leg.start_location),
          end: toLatLng(leg.end_location),
        })));
      if (ok) { show(result, status); return; }
      // Place-ID passt nicht zur Koordinate → mit Koordinaten rechnen.
      request(points.map(withoutPlaceId), show);
    });
  }, [isLoaded, pickupLocation, destinationLocation, pickupPlaceId, destinationPlaceId]);
  
  // Karte initialisieren
  const onMapLoad = useCallback((map) => {
    setMap(map);
    fitBounds(map);
  }, [fitBounds]);
  
  // Richtungen berechnen, wenn die Orte sich ändern
  useEffect(() => {
    if (showDirections && pickupLocation && destinationLocation) {
      calculateDirections();
    } else {
      setDirections(null);
    }
  }, [showDirections, pickupLocation, destinationLocation, calculateDirections]);
  
  // Neue Karte anpassen, wenn sich die Orte ändern
  useEffect(() => {
    if (map) {
      fitBounds(map);
    }
  }, [map, pickupLocation, destinationLocation, currentLocation, fitBounds]);
  
  // Lade-Fehler anzeigen
  if (loadError) {
    return (
      <Alert severity="error">
        Fehler beim Laden der Google Maps API: {loadError.message}
      </Alert>
    );
  }
  
  // Ladevorgang anzeigen
  if (!isLoaded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: height }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>
          Karte wird geladen...
        </Typography>
      </Box>
    );
  }
  
  return (
    <Paper elevation={2} sx={{ borderRadius: 1, overflow: 'hidden' }}>
      <GoogleMap
        key={mapScheme.key}
        mapContainerStyle={{ ...mapContainerStyle, height: `${height}px` }}
        center={getMapCenter()}
        zoom={12}
        options={{ ...defaultOptions, colorScheme: mapScheme.colorScheme }}
        onLoad={onMapLoad}
      >
        {/* Abholort-Marker */}
        {pickupLocation && pickupLocation.lat && pickupLocation.lng && (
          <AdvancedMarker
            position={pickupLocation}
            icon={{
              url: 'https://maps.google.com/mapfiles/ms/icons/green-dot.png',
              scaledSize: new window.google.maps.Size(32, 32)
            }}
            onClick={() => setSelectedMarker('pickup')}
          />
        )}
        
        {/* Zielort-Marker */}
        {destinationLocation && destinationLocation.lat && destinationLocation.lng && (
          <AdvancedMarker
            position={destinationLocation}
            icon={{
              url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
              scaledSize: new window.google.maps.Size(32, 32)
            }}
            onClick={() => setSelectedMarker('destination')}
          />
        )}
        
        {/* Aktuelle Position des Fahrers */}
        {currentLocation && currentLocation.lat && currentLocation.lng && (
          <AdvancedMarker
            position={currentLocation}
            icon={{
              url: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png',
              scaledSize: new window.google.maps.Size(32, 32)
            }}
            onClick={() => setSelectedMarker('current')}
          />
        )}
        
        {/* Routenlinien */}
        {directions && (
          <DirectionsRenderer
            directions={directions}
            options={{
              suppressMarkers: true,
              polylineOptions: {
                strokeColor: '#4285F4',
                strokeWeight: 5,
                strokeOpacity: 0.8
              }
            }}
          />
        )}
        
        {/* Info-Fenster für Abholort */}
        {showInfoWindows && selectedMarker === 'pickup' && pickupLocation && (
          <InfoWindow
            position={pickupLocation}
            onCloseClick={() => setSelectedMarker(null)}
          >
            <Box sx={{ p: 1, maxWidth: 200 }}>
              <Typography variant="subtitle2">Abholort</Typography>
              <Typography variant="body2">{pickupAddress || 'Keine Adresse verfügbar'}</Typography>
            </Box>
          </InfoWindow>
        )}
        
        {/* Info-Fenster für Zielort */}
        {showInfoWindows && selectedMarker === 'destination' && destinationLocation && (
          <InfoWindow
            position={destinationLocation}
            onCloseClick={() => setSelectedMarker(null)}
          >
            <Box sx={{ p: 1, maxWidth: 200 }}>
              <Typography variant="subtitle2">Zielort</Typography>
              <Typography variant="body2">{destinationAddress || 'Keine Adresse verfügbar'}</Typography>
            </Box>
          </InfoWindow>
        )}
        
        {/* Info-Fenster für aktuelle Position */}
        {showInfoWindows && selectedMarker === 'current' && currentLocation && (
          <InfoWindow
            position={currentLocation}
            onCloseClick={() => setSelectedMarker(null)}
          >
            <Box sx={{ p: 1, maxWidth: 200 }}>
              <Typography variant="subtitle2">Aktuelle Position</Typography>
              <Typography variant="body2">Standort des Fahrers</Typography>
            </Box>
          </InfoWindow>
        )}
      </GoogleMap>
      
      {/* Fehler bei der Routenberechnung */}
      {directionsError && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {directionsError}
        </Alert>
      )}
      
      {/* Legende */}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, p: 1, bgcolor: 'background.default' }}>
        {pickupLocation && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PickupIcon color="success" fontSize="small" sx={{ mr: 0.5 }} />
            <Typography variant="caption">Abholort</Typography>
          </Box>
        )}
        
        {destinationLocation && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <DestinationIcon color="error" fontSize="small" sx={{ mr: 0.5 }} />
            <Typography variant="caption">Zielort</Typography>
          </Box>
        )}
        
        {currentLocation && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <MyLocationIcon color="primary" fontSize="small" sx={{ mr: 0.5 }} />
            <Typography variant="caption">Aktuelle Position</Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default MapView;